/* eslint-disable max-statements */
/* eslint-disable camelcase */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { TieredMenu } from 'primereact/tieredmenu';
import { confirmDialog } from 'primereact/confirmdialog';
import { useGetPrepareAssociatesQuery, useGetPrepareAssociateMetricsQuery, useUpdateNbaActionedMutation, useCalculateNextBestActionAssociateMutation } from '../../coachingActionPlanSlice';
import { useStartCoachingActionPlanMutation } from '../../coachingActionSlice';
import { PrepareAssociate, PrepareAssociateMetric } from '../prepare-types';
import { selectCurrentChatAgtId, selectCurrentEmpid, selectCurrentUser, selectCurrentUserManagerLevel, setChatAgtId } from '../../../../pages/login/authSlice';
import {
    getMetricAttainmentColor,
    getMetricRecommendationImage
} from '../../../../utils/metricFormatters';
import { metricValueFormat } from '../../../../utils/metricValueFormat';
import CoachingShoutOut from '../../../../components/coachingShoutOut/coachingShoutOUt';
import { CoachingEntityTypes, CoachingTabs } from '../../../../utils/typeConstants';
import { AssociateExpandedView } from './AssociateExpandedView';
import RankB from '../../../../assets/RankB.png';
import GamePlanB from '../../../../assets/GamePlanB.png';
import { MANAGERDASHBOARD, MYMETRICS, ROLES, STACKRANK } from '../../../../constants';
import '../prepareStep.scss';
import { GameplanVertexAISummary } from '../../../gamePlan/GameplanVertexAISummary/GameplanVertexAISummary';
import { usePostNotifyPeerMutation } from '../../../../components/webRTC/notifyPeerSlice';
import { startFloatingRecording } from '../../../recordingFeature/floatingRecorderSlice';

const CAP_NAV_TOKEN_KEY = 'cap_nav_intent';
const TYPE_START_COACHING = 1;
const TYPE_RECOGNIZE = 2;
const TYPE_FOLLOW_UP = 3;
const TYPE_REVIEW = 4;
const TYPE_RECOGNIZED = 6;
const CAP_STATUS_DRAFT = 1;

// ── RecommendationCellActive ──────────────────────────────────────────────────
// Mounted only when the user clicks the badge. Fetches associate-metrics
// (session count + latestSessionId) then fires the exact same handleCtaClick
// logic as AssociateExpandedView. Zero API calls on table render.
interface ActiveProps {
    rowData: PrepareAssociate;
    scorecardVersionId: number;
    onDone: () => void;
    onShoutout: () => void;
}

const RecommendationCellActive: React.FC<ActiveProps> = ({
    rowData, scorecardVersionId, onDone, onShoutout,
}) => {
    const navigate = useNavigate();
    const authorEmployeeNumber = useSelector(selectCurrentEmpid)?.toString();
    const [startCoachingActionPlan] = useStartCoachingActionPlanMutation();

    const { data: metricsResponse } = useGetPrepareAssociateMetricsQuery(
        rowData.coaching_next_best_action_id,
        { skip: !rowData.coaching_next_best_action_id }
    );

    const metrics: PrepareAssociateMetric[] = metricsResponse?.data?.metrics ?? [];
    const selectedMetric = metrics.find(
        (m) => m.scorecard_metric_id === rowData.scorecard_metric_id
    ) ?? metrics[0] ?? null;

    const navigateToAnalyzeStep = (metric: PrepareAssociateMetric) => {
        window.scrollTo(0, 0);
        sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: 1,
                coachingActionPlanId: metric.coaching_action_plan_id,
                coachingActionPlanSessionId: metric.latest_session_id,
                employeeNumber: rowData.employee_number,
                employeeName: rowData.employee_name,
                scorecardVersionId,
                scorecardMetricId: metric.scorecard_metric_id,
                coachingNextBestActionId: rowData.coaching_next_best_action_id,
                metricName: metric.metric_name,
                metricCurrentValue: metric.metric_value ?? null,
                metricTargetValue: metric.target ?? null,
                metricTypeId: metric.metric_type_id,
                metricTypeName: metric.metric_type_name,
                metricDirectionId: metric.metric_direction_id,
                metricIndex: 0,
                metricStartDate: metric.start_date ?? null,
                metricEndDate: metric.end_date ?? null,
            },
        });
    };

    const navigateToCoachStep = (metric: PrepareAssociateMetric, isReviewSession = false) => {
        window.scrollTo(0, 0);
        sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: 2,
                isReviewSession,
                coachingActionPlanId: metric.coaching_action_plan_id,
                coachingActionPlanSessionId: metric.latest_session_id,
                employeeNumber: rowData.employee_number,
                employeeName: rowData.employee_name,
                scorecardVersionId,
                scorecardMetricId: metric.scorecard_metric_id,
                coachingNextBestActionId: rowData.coaching_next_best_action_id,
                metricName: metric.metric_name,
                metricCurrentValue: metric.metric_value ?? null,
                metricTargetValue: metric.target ?? null,
                metricTypeId: metric.metric_type_id,
                metricTypeName: metric.metric_type_name,
                metricDirectionId: metric.metric_direction_id,
                metricIndex: 0,
                metricStartDate: metric.start_date ?? null,
                metricEndDate: metric.end_date ?? null,
            },
        });
    };

    const navigateToFollowUp = (metric: PrepareAssociateMetric) => {
        window.scrollTo(0, 0);
        sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: 3,
                coachingActionPlanId: metric.coaching_action_plan_id,
                employeeNumber: rowData.employee_number,
                employeeName: rowData.employee_name,
                scorecardVersionId,
                scorecardMetricId: metric.scorecard_metric_id,
                coachingNextBestActionId: rowData.coaching_next_best_action_id,
                metricName: metric.metric_name,
                metricCurrentValue: metric.metric_value ?? null,
                metricTargetValue: metric.target ?? null,
                metricTypeId: metric.metric_type_id,
                metricTypeName: metric.metric_type_name,
                metricDirectionId: metric.metric_direction_id,
                metricIndex: 0,
            },
        });
    };

    // Fires once selectedMetric resolves — exact mirror of handleCtaClick
    React.useEffect(() => {
        if (!selectedMetric) return;

        const run = async () => {
            switch (selectedMetric.coaching_next_best_action_type_id) {
                case TYPE_RECOGNIZED:
                    // Already recognized — no navigation, just dismiss
                    break;
                case TYPE_START_COACHING:
                    if (selectedMetric.coaching_action_plan_id && selectedMetric.coaching_action_plan_status_id === CAP_STATUS_DRAFT) {
                        const res = await startCoachingActionPlan({
                            employee_number: rowData.employee_number,
                            author_employee_number: authorEmployeeNumber,
                            scorecard_metric_id: selectedMetric.scorecard_metric_id,
                            coaching_next_best_action_id: rowData.coaching_next_best_action_id,
                            session_start_date: new Date().toISOString(),
                            metric_current_value: selectedMetric.metric_value ,
                            metric_target_value: selectedMetric.target,
                            corrective_action_id: null,
                        }).unwrap();
                        console.log('Start Coaching Action Plan Result:', res);
                        navigateToAnalyzeStep(selectedMetric);
                    } else if (selectedMetric.session_count >= 1 && selectedMetric.coaching_action_plan_id) {
                        navigateToCoachStep(selectedMetric);
                    } else {
                        const res = await startCoachingActionPlan({
                            employee_number: rowData.employee_number,
                            author_employee_number: authorEmployeeNumber,
                            scorecard_metric_id: selectedMetric.scorecard_metric_id,
                            coaching_next_best_action_id: rowData.coaching_next_best_action_id,
                            session_start_date: new Date().toISOString(),
                            metric_current_value: selectedMetric.metric_value ,
                            metric_target_value: selectedMetric.target,
                            corrective_action_id: null,
                        }).unwrap();
                        sessionStorage.setItem(CAP_NAV_TOKEN_KEY, 'true');
                        navigate('/coachingActionPlan', {
                            state: {
                                activeStep: 1,
                                coachingActionPlanId: res.data.out_coaching_action_plan_id,
                                coachingActionPlanSessionId: res.data.out_coaching_action_plan_session_id,
                                employeeNumber: rowData.employee_number,
                                employeeName: rowData.employee_name,
                                scorecardVersionId,
                                scorecardMetricId: selectedMetric.scorecard_metric_id,
                                coachingNextBestActionId: rowData.coaching_next_best_action_id,
                                metricName: selectedMetric.metric_name,
                                metricCurrentValue: selectedMetric.metric_value ?? null,
                                metricTargetValue: selectedMetric.target ?? null,
                                metricTypeId: selectedMetric.metric_type_id,
                                metricTypeName: selectedMetric.metric_type_name,
                                metricDirectionId: selectedMetric.metric_direction_id,
                                metricIndex: 0,
                                metricStartDate: selectedMetric.start_date ?? null,
                                metricEndDate: selectedMetric.end_date ?? null,
                            },
                        });
                    }
                    break;
                case TYPE_RECOGNIZE:
                    if (selectedMetric.coaching_action_plan_id) {
                        navigateToFollowUp(selectedMetric);
                    } else {
                        onShoutout();
                    }
                    break;
                case TYPE_FOLLOW_UP:
                    navigateToFollowUp(selectedMetric);
                    break;
                case TYPE_REVIEW:
                    if (selectedMetric.coaching_action_plan_id && selectedMetric.session_count <= 1) {
                        navigateToCoachStep(selectedMetric, true);
                    } else {
                        navigateToFollowUp(selectedMetric);
                    }
                    break;
                default:
                    break;
            }
        };

        run().catch(() => { /* errors shown by toast in SP layer */ }).finally(() => onDone());
    }, [selectedMetric]); // eslint-disable-line

    return <i className="pi pi-spin pi-spinner recommendation-icon" />;
};

// ── RecommendationBadge ───────────────────────────────────────────────────
// Stateful wrapper that owns clicked + shoutout state.
// isHidden mirrors isCtaHidden in AssociateExpandedView (type_id === 5).
interface BadgeProps {
    rowData: PrepareAssociate;
    scorecardVersionId: number;
    readonly?: boolean;
}

const RecommendationBadge: React.FC<BadgeProps> = ({ rowData, scorecardVersionId, readonly }) => {
    const [clicked, setClicked] = useState(false);
    const [shoutoutVisible, setShoutoutVisible] = useState(false);
    const [shoutoutResult, setShoutoutResult] = useState<any>(null);
    const [updateNbaActioned] = useUpdateNbaActionedMutation();
    const [calculateNextBestAction] = useCalculateNextBestActionAssociateMutation();

    // Mirrors isCtaHidden: hidden when action type has no button (type_id 5)
    const isHidden = rowData.recommended_action_type === 'No Data'
        || rowData.recommended_action_type === 'Insufficient Data'
        || !rowData.recommended_action_type;

    // type_id 6: already actioned — render as a disabled pill, not a clickable button
    const isRecognized = rowData.recommended_action_type === 'Recognized';

    const isRecognize = rowData.recommended_action_type === 'Recognize';
    const metricsColor = isRecognize ? '#2E7D32' : '#A91D5C';

    React.useEffect(() => {
        if (shoutoutResult === 'Shoutout saved successfully') {
            updateNbaActioned({
                coachingNextBestActionId: rowData.coaching_next_best_action_id,
                scorecardMetricId: rowData.scorecard_metric_id,
            })
                .unwrap()
                .then(() => {
                    calculateNextBestAction({
                        scorecardVersionId,
                        employeeNumber: rowData.employee_number,
                    })
                        .unwrap()
                        .then(() => {
                            setShoutoutResult(null);
                        })
                        .catch((nbaError: any) => {
                            console.error('NBA recalculation failed:', nbaError);
                            setShoutoutResult(null);
                        });
                })
                .catch((error: any) => {
                    console.error('Failed to update NBA actioned:', error);
                    toast.error('Failed to update action status.');
                    setShoutoutResult(null);
                });
        }
    }, [shoutoutResult]);

    const getBadgeClass = (actionType: string) => {
        switch (actionType) {
            case 'Start Coaching':    return 'recommendation-badge-danger';
            case 'Follow Up':         return 'recommendation-badge-warning';
            case 'Recognize':         return 'recommendation-badge-success';
            case 'Recognized':        return 'recommendation-badge-recognized';
            case 'Review':            return 'recommendation-badge-review';
            case 'Insufficient Data': return 'recommendation-badge-insufficient';
            default:                  return 'recommendation-badge-review';
        }
    };

    const trendIcon = getMetricRecommendationImage(rowData.metric_attained ? 1 : 2);

    const renderRecommendationBadge = () => {
        if (isHidden) {
            return (
                <div className={`recommendation-badge ${getBadgeClass(rowData.recommended_action_type ?? 'Insufficient Data')}`}>
                    <span className="recommendation-text">{rowData.recommended_action_type ?? 'Insufficient Data'}</span>
                </div>
            );
        }
        if (isRecognized) {
            // TYPE_RECOGNIZED (6): completed state — visually distinct pill, not clickable
            const tooltipId = `recognized-tooltip-${rowData.employee_number}`;
            return (
                <>
                    <Tooltip target={`#${tooltipId}`} content="This metric was recently recognized" position="top" />
                    <div
                        id={tooltipId}
                        className="recommendation-badge recommendation-badge-recognized"
                    >
                        <i className="pi pi-check recommendation-icon" />
                        <span className="recommendation-text">{rowData.recommended_action_type ?? 'Recognized'}</span>
                    </div>
                </>
            );
        }
        return (
            <button
                className={`recommendation-badge ${getBadgeClass(rowData.recommended_action_type)}`}
                style={{ cursor: 'pointer', textAlign: 'left' }}
                onClick={(e) => { e.stopPropagation(); setClicked(true); }}
                disabled={clicked || readonly}
            >
                <span className="recommendation-text">{rowData.recommended_action_type}</span>
                {readonly === false && (<>
                <div className="recommendation-separator" />
                {clicked
                    ? <RecommendationCellActive
                        rowData={rowData}
                        scorecardVersionId={scorecardVersionId}
                        onDone={() => setClicked(false)}
                        onShoutout={() => { setClicked(false); setShoutoutVisible(true); }}
                    />
                    : <i className="pi pi-arrow-circle-right recommendation-icon" />
                }
                </>)}
            </button>
        );
    };

    return (
        <>
            <div className="flex flex-column gap-3">
                {renderRecommendationBadge()}

                {rowData.metrics_at_risk_count > 0 && (
                    <div className="flex align-items-center gap-2">
                        {/* <i className="pi pi-arrow-down-right" style={{ color: metricsColor, fontSize: '1rem' }} /> */}
                        {trendIcon && <img src={trendIcon} alt="trend" width="18" height="18" />}
                        <span className="font-bold" style={{ color: metricsColor, fontSize: '1rem', marginLeft: '0.25rem' }}>
                            {rowData.metrics_at_risk_count} {rowData.metrics_at_risk_count === 1 ? 'Metric' : 'Metrics'} Not Attained
                        </span>
                    </div>
                )}
            </div>

            {shoutoutVisible && (
                <CoachingShoutOut
                    receiverId={rowData.employee_number}
                    receiverName={rowData.employee_name}
                    visible={shoutoutVisible}
                    setVisible={setShoutoutVisible}
                    // coachingEntityId={rowData.coaching_action_plan_id}
                    coachingEntityId={rowData.scorecard_metric_id}
                    scorecardMetricId={rowData.scorecard_metric_id}
                    coachingEntityTypeId={CoachingEntityTypes.CoachingMetricRecognition}
                    coreValueId={0}
                    via={CoachingTabs.GamePlan}
                    result={setShoutoutResult}
                />
            )}
        </>
    );
};

interface AssociateScoresTableProps {
    scorecardVersionId: number;
    teamLeadId?: number;
    via?: string;
}

export const createMenuItemTemplate = (alt: string, label: string, onClick: () => void) => {
    const getImageByLabel = (label: string) => {
        switch (label) {
            case 'Ranking':
                return RankB;
            case 'My Metrics':
                return GamePlanB;
            default:
                return '';
        }
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };
    const imageSrc = getImageByLabel(label);

    return () => (
        <div
            className="p-menuitem-link"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={handleKeyDown}
        >
            <img src={imageSrc} alt={alt} style={{ width: 16, marginRight: 8 }} />
            <span>{label}</span>
        </div>
    );
};

export const AssociateScoresTable: React.FC<AssociateScoresTableProps> = ({ scorecardVersionId, teamLeadId, via }) => {
    const teamLeadEmployeeNumber = useSelector(selectCurrentEmpid);
    const { data: response, isLoading, error } = useGetPrepareAssociatesQuery(
        { teamLeadEmployeeNumber: (via === 'managerCAP' && teamLeadId) ? teamLeadId : teamLeadEmployeeNumber, scorecardVersionId },
        { skip: !teamLeadEmployeeNumber || scorecardVersionId <= 0 }
    );
    const [expandedRows, setExpandedRows] = useState<any>(null);

    const associates = response?.data || [];

    const dispatch = useDispatch();
    const [postNotifyPeer] = usePostNotifyPeerMutation();
    const userManagerLevel = useSelector(selectCurrentUserManagerLevel);
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const CurrentChatAgtId = useSelector(selectCurrentChatAgtId);
    const loggedEmpUserName = useSelector(selectCurrentUser);
    const [summaryVisible, setSummaryVisible] = useState(false);
    const [seletedRowData, setSeletedRowData] = useState<any>();
    const navigate = useNavigate();
    const menu = useRef<TieredMenu>(null);
    const authFullState: any = useSelector((state: any) => state.auth);

    const teamLeadFullName = authFullState?.userNameWithId
        ? (() => {
            const nameOnly = authFullState.userNameWithId.replace(/\s*\(\d+\)/, '').trim();
            const parts = nameOnly.split(',').map((p: string) => p.trim());
            return parts.length === 2 ? `${parts[1]} ${parts[0]}` : loggedEmpUserName;
        })()
        : loggedEmpUserName;


    const onRankingClicked = () => {
        navigate(
            `${STACKRANK}/${seletedRowData?.employee_number}/${scorecardVersionId}/${seletedRowData?.year}/${seletedRowData?.month}`,
            { replace: true }
        );
    };
    const onMyMetricsClicked = () => {
        window.scrollTo(0, 0);
        if (ROLES.Supervisor2AndAbove.includes(userManagerLevel)) {
            navigate(
                `${MANAGERDASHBOARD}/0/${scorecardVersionId}/${seletedRowData?.year}-${seletedRowData?.month}/${seletedRowData?.employee_number}`,
                { replace: true }
            );
        } else {
            navigate(
                `${MYMETRICS}/${seletedRowData?.employee_number}/${scorecardVersionId}/${seletedRowData?.year}-${seletedRowData?.month}`,
                { replace: true }
            );
        }
    };
    const onVoiceRecordingClicked = () => {
        dispatch(startFloatingRecording({
            callerId: loggedEmpNum,
            calleeId: seletedRowData?.employee_number ?? '',
            associateName: seletedRowData?.employee_name ?? '',
        }));
    };

    const onSummaryCliked = () => {
        setSummaryVisible(true);
    };

    const startCall = async (callerId: string, calleeId: string, calleeName: string) => {
        console.log(`Notification sending to : ${calleeId}`);
        toast.success(`Notification sending to : ${calleeName}`);

        try {
            await postNotifyPeer({
                callerId,
                calleeId,
                //    message: `${currentUserName.replace(/\s*\(Impersonating\)\s*/g, ' ').trim()} is requesting a call`,
                message: `${teamLeadFullName.replace(/\s*\(Impersonating\)\s*/g, ' ').trim()} is requesting a call`,
            }).unwrap()
                .then((response: any) => {
                    if (response.message === "callee_not_connected") {
                        toast.warn(`${calleeName} is off-line.`);
                        window.dispatchEvent(new CustomEvent('agentOffline', { detail: { offline: true } }));
                    }
                    if (response.message === "notification_sent") {
                        toast.success(`Notification sent to ${calleeName}.`);
                    }
                })
                .catch((response: any) => {
                    console.error('Error sending notification:', response);
                    toast.error(`Failed to send notification to ${calleeName}.`);
                });
        } catch (error) {
            toast.error(`Failed to send notification to ${calleeName}.`);
            console.error("Failed to send notify request", error);
        }




    };

    const onInitiateCallClicked = () => {
        const empId = seletedRowData?.employee_number;
        const empName = seletedRowData?.employee_name;
        console.log('Row-Clicked : Initiating call for empId:', empId);

        // Check if a call is already active
        if (CurrentChatAgtId) {
            const currentAgentName = sessionStorage.getItem('chatAgentName') || 'another agent';
            confirmDialog({
                header: null,
                message: (
                    <div className="dialog-message">
                        <i className="pi pi-exclamation-triangle gradient-icon" style={{ color: '#ff9800' }} />
                        <strong>Call Already in Progress!</strong>
                        <p style={{ marginTop: '8px' }}>You are currently on a call with {currentAgentName}. Please end your current call before starting a new one.</p>
                    </div>
                ),
                icon: null,
                acceptLabel: 'End Current Call & Start New',
                rejectLabel: 'Cancel',
                acceptClassName: 'custom-accept-btn',
                rejectClassName: 'custom-reject-btn',
                position: 'center',
                accept: () => {
                    console.log('Ending current call and starting new call');
                    // End current call first
                    dispatch(setChatAgtId(null));
                    sessionStorage.removeItem('chatAgentName');

                    // Wait for cleanup, then start new call
                    setTimeout(() => {
                        if (empId) {
                            dispatch(setChatAgtId(empId));
                            sessionStorage.setItem('chatAgentName', empName);
                            startCall(loggedEmpNum, empId, empName);
                        }
                    }, 500);
                },
                reject: () => {
                    console.log('User cancelled starting new call');
                },
            });
            return;
        }

        // No active call, proceed normally
        if (empId) {
            dispatch(setChatAgtId(empId));
            sessionStorage.setItem('chatAgentName', empName);
            startCall(loggedEmpNum, empId, empName);
        } else {
            dispatch(setChatAgtId(null));
            sessionStorage.removeItem('chatAgentName');
        }
    };

    const items = [];
    items.push(
        {
            label: 'Ranking',
            template: createMenuItemTemplate('Ranking', 'Ranking', onRankingClicked),
            // visible: !!hideMyMetricRankingMenu,
        },
        // { separator: showSeparator(), visible: showSeparator() },
        {
            label: 'My Metrics',
            template: createMenuItemTemplate('My Metrics', 'My Metrics', onMyMetricsClicked),
            // visible: !!hideMyMetricRankingMenu,
        },
        {
            label: 'Summary',
            icon: 'pi pi-user',
            command: () => {
                onSummaryCliked();
            },
        },

    );


    items.push(
        {
            label: 'Recording',
            icon: 'pi pi-stop-circle',
            items: [
                {
                    label: 'Call',
                    icon: 'pi pi-phone',
                    command: () => {
                        onInitiateCallClicked();
                    },
                },
                {
                    label: 'Side by Side',
                    icon: 'pi pi-users',
                    command: () => {
                        onVoiceRecordingClicked();
                    },
                },
            ],
        }
    );

    const renderSummaryModals = () => {
        return (
            <GameplanVertexAISummary
                setDisplayModal={setSummaryVisible}
                displayModal={summaryVisible}
                authorId={loggedEmpNum}
                seletedRowData={seletedRowData}
            />
        );
    };

    // Column 1: Is Actioned indicator — green checkbox when actioned, grey when not (no expander)
    const actionedIndicatorTemplate = (rowData: PrepareAssociate) => {
        const iconClass = rowData.is_actioned
            ? 'pi pi-check-square actioned-icon actioned-icon--actioned'
            : 'pi pi-stop actioned-icon actioned-icon--pending';

        return (
            <div className="actioned-expander-cell">
                <i className={iconClass} />
            </div>
        );
    };

    // Column 2: User Name with avatar
    const nameBodyTemplate = (rowData: PrepareAssociate) => {
        const initials = rowData.employee_name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();

        return (
            <div style={{ display: 'flex', alignItems: 'anchor-center', gap: '0.75rem' }}>
                <div className="associate-avatar">{initials}</div>
                <span className="associate-name">
                    {rowData.employee_name} <span className="employee-number">({rowData.employee_number})</span>
                </span>
            </div>
        );
    };

    // Column 3: Recommended Metric with direction icon and color
    const recommendedMetricBodyTemplate = (rowData: PrepareAssociate) => {
        // const trendIcon = getMetricDirectionImage(rowData.recommended_metric_direction_id);
        const trendIcon = getMetricRecommendationImage(rowData.metric_attained ? 1 : 2);
        const colorClass = getMetricAttainmentColor(rowData.metric_attained);

        return (
            <div className={`flex align-items-center gap-3 ${colorClass}`}>
                {trendIcon && <img src={trendIcon} alt="trend" width="24" height="24" />}
                <span className="font-bold" style={{ fontSize: '1.6rem', marginLeft: '0.5rem' }}>
                    {rowData.recommended_metric_name}
                </span>
            </div>
        );
    };

    // Column 4: Last 30 Day Rollup - "80% / 100%" format with colored pill background
    const rollupBodyTemplate = (rowData: PrepareAssociate) => {
        if (rowData.recommended_metric_value === null || rowData.recommended_metric_target === null) {
            return <span className="text-color-secondary">N/A</span>;
        }

        const valueFormatted = metricValueFormat(
            rowData.recommended_metric_value,
            rowData.recommended_metric_type_name
        );
        const targetFormatted = metricValueFormat(
            rowData.recommended_metric_target,
            rowData.recommended_metric_type_name
        );

        const pillClass = rowData.metric_attained ? 'rollup-pill-success' : 'rollup-pill-danger';

        return (
            <div className="flex align-items-center gap-3">
                <div className={`rollup-pill ${pillClass}`}>
                    {valueFormatted}
                </div>
                <span className="rollup-target">/ {targetFormatted}</span>
            </div>
        );
    };

    // Column 5: Recommendation — clickable badge, mirrors AssociateExpandedView handleCtaClick exactly
    const recommendationBodyTemplate = (rowData: PrepareAssociate) => {
        return (
            <RecommendationBadge rowData={rowData} scorecardVersionId={scorecardVersionId} readonly={via === 'managerCAP'} />
        );
    };

    // Column 6: Active Action Plans
    const activeActionPlanBodyTemplate = (rowData: PrepareAssociate) => {
        // const isRecognize = rowData.recommended_action_type === 'Recognize';

        if (rowData.coaching_action_plan_status_id === 3) { // Finished status
            return (
                <div className="cap-status cap-status--finished">
                    <i className="pi pi-check" />
                    <span>Action Plan Finished</span>
                </div>
            );
        }

        const hasActivePlan = rowData.active_action_plan_count > 0;
        if (!hasActivePlan) {
            return (
                <div className="cap-status cap-status--none">
                    <span className="cap-status__dash">—</span>
                    <span>No Active Action Plan</span>
                </div>
            );
        }

        return (
            <div className="cap-status cap-status--active">
                <div className="cap-status__count">
                    <i className="pi pi-chart-line" />
                    <span>{rowData.active_action_plan_count} Active Action Plan{rowData.active_action_plan_count > 1 ? 's' : ''}</span>
                </div>
                {rowData.next_follow_up_date && (
                    <span className="cap-status__followup">
                        Follow Up on {new Date(rowData.next_follow_up_date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                    </span>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="p-4 text-center">
                <i className="pi pi-spin pi-spinner text-4xl text-primary" />
                <p className="mt-3 text-color-secondary">Loading associates...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-center">
                <i className="pi pi-exclamation-triangle text-4xl text-red-500" />
                <p className="mt-3 text-red-500 font-semibold">
                    Unable to load associate data. Please try again later.
                </p>
            </div>
        );
    }

    if (associates.length === 0) {
        return (
            <div className="p-4 text-center">
                <i className="pi pi-users text-4xl text-color-secondary" />
                <p className="mt-3 text-color-secondary">No associates currently assigned</p>
            </div>
        );
    }

    const rowExpansionTemplate = (rowData: PrepareAssociate) => (
        <AssociateExpandedView associate={rowData} scorecardVersionId={scorecardVersionId} readonly={via === 'managerCAP'} />
    );

    const popupMenuClickHandler = (e: any, rowData: any) => {
        const date = rowData?.end_date ? new Date(rowData.end_date) : new Date();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        const enrichedRowData = { ...rowData, month, year };

        console.log('Row Data', enrichedRowData);
        e.preventDefault();
        setSeletedRowData(enrichedRowData);
        menu.current?.toggle(e);
    };

    const actionTemplate = (rowData: any) => {
        return (
            <Button
                style={{ color: '#000', background: 'none', border: 'none', boxShadow: 'none' }}
                type="button"
                icon="pi pi-ellipsis-v"
                onClick={(e: any) => {
                    popupMenuClickHandler(e, rowData);
                }}
            />
        );
    };

    return (
        <div className="associate-scores-table">
            <TieredMenu model={items} popup ref={menu} id="popup_menu" className="headerFeatures" />
            <DataTable
                value={associates}
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="employee_number"
                className="p-datatable-sm associate-scores-datatable"
                responsiveLayout="scroll"
                rowClassName={(rowData: PrepareAssociate) =>
                    rowData.is_actioned ? 'associate-row associate-row--actioned' : 'associate-row'
                }
            >
                {/* Column 1: Is Actioned indicator — green tint on this cell only */}
                <Column
                    body={actionedIndicatorTemplate}
                    style={{ width: '3rem' }}
                    bodyClassName={(rowData: PrepareAssociate) =>
                        rowData.is_actioned ? 'actioned-cell' : ''
                    }
                />
                <Column
                    field="employee_name"
                    header="Associate"
                    body={nameBodyTemplate}
                    sortable
                    style={{ minWidth: '220px' }}
                />
                <Column
                    header="Recommended Metric"
                    body={recommendedMetricBodyTemplate}
                    sortable
                    sortField="recommended_metric_name"
                    style={{ minWidth: '200px' }}
                />
                <Column
                    header="Last 30 Day Rollup"
                    body={rollupBodyTemplate}
                    sortable
                    sortField="recommended_metric_value"
                    style={{ minWidth: '200px' }}
                />
                <Column
                    header="Recommendation"
                    body={recommendationBodyTemplate}
                    sortable
                    sortField="recommended_action_type"
                    style={{ minWidth: '250px' }}
                />
                <Column
                    header="Active Action Plan"
                    body={activeActionPlanBodyTemplate}
                    sortable
                    sortField="active_action_plan_count"
                    style={{ minWidth: '250px' }}
                />
                {via !== 'managerCAP' && (
                    <Column
                        body={actionTemplate}
                        headerStyle={{ width: '2rem', padding: 0 }}
                        style={{ minWidth: '2rem', padding: 0 }}
                    />
                )}
                {/* Last column: row expander chevron */}
                <Column
                    expander
                    style={{ width: '3rem' }}
                />
            </DataTable>
            {summaryVisible && renderSummaryModals()}
        </div>
    );
};